import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MessageSquareWarning, Check, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const TYPES = [
  { v: 'peticion', l: 'Petición', desc: 'Solicitud de información o servicio' },
  { v: 'queja', l: 'Queja', desc: 'Inconformidad con la atención recibida' },
  { v: 'reclamo', l: 'Reclamo', desc: 'Reclamación sobre un producto o servicio' },
  { v: 'sugerencia', l: 'Sugerencia', desc: 'Propuesta de mejora' },
];

export default function PQR() {
  const { user } = useAuth();
  const [f, setF] = useState({
    type: 'peticion',
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    subject: '',
    message: '',
    order_id: '',
  });
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState(null);

  useSEO({
    title: 'Radica tu PQR | AutoMarket Colombia',
    description: 'Petición, queja, reclamo o sugerencia. Te respondemos en máximo 48 horas hábiles.',
    canonical: '/pqr',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.subject.trim() || !f.message.trim()) {
      toast.error('Asunto y mensaje son obligatorios');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/pqr', f);
      setTicket(data.ticket_number || data.pqr_id);
      toast.success('PQR registrada correctamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
    } finally { setBusy(false); }
  };

  if (ticket) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8 text-center" data-testid="pqr-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center bg-emerald-600 text-white">
            <Check className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-black tracking-tighter text-slate-900">PQR recibida</h1>
          <p className="mt-3 text-slate-600">Te responderemos en máximo 48 horas hábiles al correo <span className="font-semibold text-slate-900">{f.email}</span>.</p>
          <div className="mt-6 inline-block border border-slate-200 bg-slate-50 px-6 py-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Número de radicado</div>
            <div className="font-mono text-lg font-black text-slate-900 mt-0.5">{ticket}</div>
          </div>
          <div className="mt-8 flex items-center justify-center gap-6">
            <Link to="/mis-pqr" data-testid="pqr-my-link" className="inline-flex items-center gap-2 text-blue-700 hover:underline font-semibold">
              Ver mis PQR →
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-blue-700 hover:underline font-semibold">
              <ArrowLeft className="h-4 w-4" /> Volver al inicio
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Soporte</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-3">
          <MessageSquareWarning className="h-7 w-7 text-slate-600" /> Radica tu PQR
        </h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Peticiones, quejas, reclamos y sugerencias. Registramos cada caso con un número de radicado y te respondemos en máximo 48 horas hábiles.
        </p>

        <form onSubmit={submit} className="mt-8 border border-slate-200 bg-white p-6 space-y-4" data-testid="pqr-form">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Tipo de solicitud</label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setF({ ...f, type: t.v })}
                  data-testid={`pqr-type-${t.v}`}
                  className={`p-3 text-left border-2 transition-colors ${
                    f.type === t.v
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-500'
                  }`}
                >
                  <div className="font-bold text-sm">{t.l}</div>
                  <div className={`text-[10px] mt-0.5 ${f.type === t.v ? 'text-slate-300' : 'text-slate-500'}`}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Nombre completo *</label>
              <Input data-testid="pqr-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Correo electrónico *</label>
              <Input data-testid="pqr-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Teléfono</label>
              <Input data-testid="pqr-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">N° de orden (opcional)</label>
              <Input data-testid="pqr-order" placeholder="ord_..." value={f.order_id} onChange={(e) => setF({ ...f, order_id: e.target.value })} className="rounded-none mt-1 font-mono" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Asunto *</label>
            <Input data-testid="pqr-subject" required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Mensaje *</label>
            <Textarea data-testid="pqr-message" required rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="rounded-none mt-1" />
          </div>

          <Button
            data-testid="pqr-submit-btn"
            type="submit"
            disabled={busy}
            className="rounded-none bg-slate-900 hover:bg-slate-800 h-11 px-8 font-bold"
          >
            {busy ? 'Enviando…' : 'Enviar PQR'}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
